import React from "react";
import { useTranslation } from "react-i18next";
import {
  ArticleOutlined,
  DashboardOutlined,
  EditNoteOutlined,
  FactCheckOutlined,
  InboxOutlined,
  ListAltOutlined,
} from "@mui/icons-material";
import { Features, UserRole } from "../../../Enums/companyStatus";
import { hasPermission, hasRole } from "../../../Helpers/authHelpers";
import { NavItem } from "../../DashboardMenu/navigationItems";

export const AMBASSADOR_PATH = "/ambassador";

export const AMBASSADOR_FEATURES = [
  Features.AMBASSADOR,
  Features.AMBASSADOR_DEAL_MANAGEMENT,
  Features.AMBASSADOR_NEWS,
];

export const ambassadorPaths = {
  dashboard: `${AMBASSADOR_PATH}/dashboard`,
  myDeals: `${AMBASSADOR_PATH}/deals`,
  newDeal: `${AMBASSADOR_PATH}/new-deal`,
  myDeal: (dealId: number) => `${AMBASSADOR_PATH}/deals/${dealId}`,
  editDraft: (dealId: number) => `${AMBASSADOR_PATH}/deals/${dealId}/edit`,
  news: `${AMBASSADOR_PATH}/news`,
  newsItem: (newsId: number) => `${AMBASSADOR_PATH}/news/${newsId}`,
  inbox: `${AMBASSADOR_PATH}/manage/inbox`,
  allDeals: `${AMBASSADOR_PATH}/manage/deals`,
  review: (dealId: number) => `${AMBASSADOR_PATH}/manage/deals/${dealId}`,
  manageNews: `${AMBASSADOR_PATH}/manage/news`,
};

/** Which parts of the Ambassador Workspace the user can open; admins all. */
export const useAmbassadorAccess = () => {
  const isAdmin = hasRole(UserRole.ADMIN);
  const can = (feature: Features) => isAdmin || hasPermission(feature);
  return {
    isAdmin,
    isAmbassador: can(Features.AMBASSADOR),
    isDealManager: can(Features.AMBASSADOR_DEAL_MANAGEMENT),
    isNewsEditor: can(Features.AMBASSADOR_NEWS),
  };
};

/** The module's entries in the main sidebar: the ambassador's pages, then
 * como's. */
export const useAmbassadorNavigation = () => {
  const { t } = useTranslation("ambassador");
  const { isAmbassador, isDealManager, isNewsEditor } = useAmbassadorAccess();

  const newsItem: NavItem = {
    key: "ambassadorNews",
    path: ambassadorPaths.news,
    label: t("navigation.news"),
    icon: <ArticleOutlined />,
  };

  const ambassador: NavItem[] = isAmbassador
    ? [
        {
          key: "ambassadorDashboard",
          path: ambassadorPaths.dashboard,
          label: t("navigation.dashboard"),
          icon: <DashboardOutlined />,
        },
        {
          key: "ambassadorMyDeals",
          path: ambassadorPaths.myDeals,
          label: t("navigation.myDeals"),
          icon: <ListAltOutlined />,
          // New deals are started from My Deals or the dashboard.
          activePrefixes: [ambassadorPaths.newDeal],
        },
        newsItem,
      ]
    : [];

  const como: NavItem[] = [
    ...(isDealManager
      ? [
          {
            key: "ambassadorInbox",
            path: ambassadorPaths.inbox,
            label: t("navigation.inbox"),
            icon: <InboxOutlined />,
          },
          {
            key: "ambassadorAllDeals",
            path: ambassadorPaths.allDeals,
            label: t("navigation.allDeals"),
            icon: <FactCheckOutlined />,
          },
        ]
      : []),
    ...(isNewsEditor
      ? [
          {
            key: "ambassadorManageNews",
            path: ambassadorPaths.manageNews,
            label: t("navigation.manageNews"),
            icon: <EditNoteOutlined />,
          },
        ]
      : []),
    // como users who are no ambassadors read the news here.
    ...(!isAmbassador ? [newsItem] : []),
  ];

  return { ambassador, como };
};

/** Where /ambassador leads: the first page the user can open. */
export const useAmbassadorHomePath = () => {
  const { isAmbassador, isDealManager } = useAmbassadorAccess();
  if (isAmbassador) return ambassadorPaths.dashboard;
  if (isDealManager) return ambassadorPaths.inbox;
  return ambassadorPaths.manageNews;
};
